import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import { useTaskTypes } from "components/Actions/dialogs/Devices/CreateTask/hooks";
import ScheduleTask from "components/ScheduleTask";
import { HeadTitleInfo } from "components/Table/components/HeadTitleInfo";
import {
    FEATURE_SETTINGS,
    TaskGroupsLoader,
    useCompanyLevelSettings,
    usePermissions
} from "shared";

import File from "../components/File";
import { hasPermissionAndLoading, isFileAndSchedule } from "./functions";
import { SubtitleProps } from "./types";

const Subtitle = ({ data, isLoading, file, fileId }: SubtitleProps) => {
    const { gray900, textDisabled, textPlaceholder } = colors;

    const { t } = useTranslation();
    const { isFeatureSettingsRestricted } = useCompanyLevelSettings();
    const { isTaskFileDeleted } = useTaskTypes();
    const { isReadTaskNotAllowed } = usePermissions();

    const { id, schedule } = data;

    const scheduleTimeRestriction = FEATURE_SETTINGS.TaskScheduleSupport;

    const isScheduleTimeRestricted = isFeatureSettingsRestricted(
        scheduleTimeRestriction
    );

    const isFileAndScheduleExist = isFileAndSchedule(fileId, schedule);
    const isDeletedFile = isTaskFileDeleted(fileId, file);

    const isAllowedAndLoading = hasPermissionAndLoading(
        !isReadTaskNotAllowed,
        isLoading
    );

    return (
        <div
            css={css({
                display: "flex",
                alignItems: "center",
                columnGap: "16px",
                rowGap: "4px",
                marginTop: "4px",
                overflowX: "hidden",

                "@media(max-width: 839px)": {
                    flexDirection: isFileAndScheduleExist ? "column" : "row",
                    alignItems: isFileAndScheduleExist ? "flex-start" : "center"
                },

                "@media(max-width: 599px)": {
                    flexDirection:
                        isFileAndScheduleExist || isDeletedFile
                            ? "column"
                            : "row",
                    alignItems:
                        isFileAndScheduleExist || isDeletedFile
                            ? "flex-start"
                            : "center"
                }
            })}
        >
            {isAllowedAndLoading ? (
                <TaskGroupsLoader
                    height="20px"
                    firstWidth="52px"
                    secondWidth="200px"
                />
            ) : (
                <>
                    <div
                        css={css({
                            display: "flex",
                            columnGap: "16px",

                            div: {
                                display: "flex",
                                alignItems: "center"
                            },

                            span: {
                                display: "inline-flex",
                                fontSize: "14px",
                                fontWeight: "500",
                                lineHeight: "20px",
                                letterSpacing: "0.1px",
                                whiteSpace: "nowrap"
                            },

                            "span:not(.schedule-time span):nth-of-type(1)": {
                                color: textPlaceholder,
                                paddingRight: isAllowedAndLoading ? 0 : "5px"
                            },

                            "span:not(.schedule-time span):nth-of-type(2)": {
                                color: gray900
                            }
                        })}
                    >
                        <div>
                            <span>{t("General##id")}:</span>
                            <span>{id}</span>
                        </div>

                        {schedule && (
                            <div className="schedule-time">
                                {isScheduleTimeRestricted && (
                                    <HeadTitleInfo
                                        restriction={scheduleTimeRestriction}
                                        css={css({
                                            marginRight: "2px"
                                        })}
                                    />
                                )}

                                <span
                                    css={css({
                                        color: isScheduleTimeRestricted
                                            ? textDisabled
                                            : textPlaceholder
                                    })}
                                >
                                    {t("Table##schedule")}:
                                </span>

                                <span
                                    css={css({
                                        paddingLeft: "5px",
                                        color: isScheduleTimeRestricted
                                            ? textDisabled
                                            : gray900
                                    })}
                                >
                                    <ScheduleTask
                                        schedule={schedule}
                                        format="L"
                                        shouldBeAdjusted
                                    />
                                </span>
                            </div>
                        )}
                    </div>

                    {fileId && <File data={file} isSubtitle />}
                </>
            )}
        </div>
    );
};

export default Subtitle;
